/**
 * MMT Application Cookie Utilities
 * Type-safe getters for all MMT-specific cookies
 */

import { getCookie } from './cookies';

/**
 * Cookie name constants for MMT application
 */
export const COOKIE_NAMES = {
  AUTH: 'mmt-auth', // MMT sets this cookie (with hyphen)
  COUNTRY_CODE: 'ccde',
  DEVICE_ID: 'dvid',
  LANGUAGE: 'lang',
  UUID: 'uuid',
  CURRENCY: 'userCurrency',
} as const;

/**
 * Type-safe interface for all MMT cookies
 */
export interface MMTCookies {
  readonly auth: string | null;
  readonly countryCode: string | null;
  readonly deviceId: string | null;
  readonly language: string | null;
  readonly uuid: string; // Returns empty string if not set
  readonly currency: string | null;
}

/**
 * Get MMT authentication token.
 * Returns null if not logged in — callers must handle the unauthenticated case.
 */
export function getAuthToken(): string | null {
  return getCookie(COOKIE_NAMES.AUTH);
}

/**
 * Get country code from cookies
 */
export function getCountryCode(): string | null {
  return getCookie(COOKIE_NAMES.COUNTRY_CODE);
}

/**
 * Get device ID from cookies
 */
export function getDeviceId(): string | null {
  return getCookie(COOKIE_NAMES.DEVICE_ID);
}

/**
 * Get language preference from cookies
 */
export function getLanguage(): string | null {
  return getCookie(COOKIE_NAMES.LANGUAGE);
}

/**
 * Get user UUID from cookies
 * Returns empty string if UUID doesn't exist (caller should handle auth check)
 */
export function getUUID(): string {
  return getCookie(COOKIE_NAMES.UUID) ?? '';
}

/**
 * Get user currency preference from cookies
 */
export function getCurrency(): string | null {
  return getCookie(COOKIE_NAMES.CURRENCY);
}

/**
 * Get all MMT cookies at once
 * Useful for batch operations or debugging
 */
export function getAllMMTCookies(): MMTCookies {
  return {
    auth: getAuthToken(),
    countryCode: getCountryCode(),
    deviceId: getDeviceId(),
    language: getLanguage(),
    uuid: getUUID(),
    currency: getCurrency(),
  };
}
